/**
 * SlideCanvasController — Reusable slide / element management logic
 *
 * Encapsulates: slides, elements, drag/resize, layering, brand kit, templates, undo/redo.
 *
 * Usage in view.ts:
 *   import { SlideCanvasController } from '@wiz/libs/portal/editor-core/canvas';
 *   import { HistoryManager }        from '@wiz/libs/portal/editor-core/history';
 *
 *   export class Component implements OnInit, OnDestroy {
 *     public canvas: SlideCanvasController;
 *     public history = new HistoryManager(50);
 *
 *     public async ngOnInit() {
 *       this.canvas = new SlideCanvasController(() => this.service.render(), this.history);
 *       const { code, data } = await wiz.call('load');
 *       if (code === 200) this.canvas.load(data);
 *     }
 *
 *     public ngOnDestroy() { this.canvas.destroy(); }
 *
 *     // Save
 *     public async save() {
 *       await wiz.call('save', { state: JSON.stringify(this.canvas.toSavePayload()) });
 *     }
 *   }
 *
 * In template bind via `canvas.*`:
 *   *ngFor="let slide of canvas.slides"
 *   [style]="canvas.elementStyle(el)"
 */

import { HistoryManager } from '@wiz/libs/portal/editor-core/history';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SlideElement {
    id: string;
    type: 'text' | 'image' | 'shape';
    x: number;
    y: number;
    w: number;
    h: number;
    zIndex: number;
    locked: boolean;
    // Text
    text?: string;
    fontSize?: number;
    fontWeight?: string;
    fontFamily?: string;
    color?: string;
    textAlign?: string;
    italic?: boolean;
    underline?: boolean;
    // Image
    src?: string;
    objectFit?: string;
    // Shape
    shapeType?: string;     // rect | circle | triangle
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    borderRadius?: number;
}

export interface Slide {
    id: string;
    bg: string;
    elements: SlideElement[];
}

export interface BrandKit {
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    logoUrl: string;
}

export interface CanvasSnapshot {
    slides: Slide[];
    selectedSlideIndex: number;
    selectedElementId: string | null;
}

// ─── Controller ──────────────────────────────────────────────────────────────

export class SlideCanvasController {
    // ── State (bind directly in templates) ───────────────────────────────
    public slides: Slide[] = [];
    public selectedSlideIndex: number = 0;
    public selectedElementId: string | null = null;
    public brand: BrandKit = { primaryColor: '#3b82f6', secondaryColor: '#f3f4f6', fontFamily: 'sans-serif', logoUrl: '' };
    public dirty: boolean = false;

    // ── Drag / resize state ───────────────────────────────────────────────
    private _drag: { el: SlideElement; startX: number; startY: number; ox: number; oy: number } | null = null;
    private _resize: { el: SlideElement; handle: string; startX: number; startY: number; ow: number; oh: number; ox: number; oy: number } | null = null;
    private _boundMouseMove = this._onMouseMove.bind(this);
    private _boundMouseUp   = this._onMouseUp.bind(this);

    constructor(
        private _render: () => Promise<void>,
        private _history: HistoryManager<CanvasSnapshot>,
        private _onError?: (msg: string) => Promise<void>,
    ) {}

    // ── Computed ─────────────────────────────────────────────────────────

    get currentSlide(): Slide | null  { return this.slides[this.selectedSlideIndex] ?? null; }
    get slideCount(): number           { return this.slides.length; }
    get canUndo(): boolean             { return this._history.canUndo; }
    get canRedo(): boolean             { return this._history.canRedo; }

    get selectedElement(): SlideElement | null {
        if (!this.currentSlide || !this.selectedElementId) return null;
        return this.currentSlide.elements.find(e => e.id === this.selectedElementId) ?? null;
    }

    get elements(): SlideElement[] { return this.currentSlide?.elements ?? []; }

    // ── Load / Save ──────────────────────────────────────────────────────

    load(data: any): void {
        this.slides              = (data.slides || []).map((s: any) => this._normalizeSlide(s));
        this.selectedSlideIndex  = Number(data.selectedSlideIndex || 0);
        this.selectedElementId   = data.selectedElementId ?? null;
        if (data.brand) {
            this.brand = { ...this.brand, ...data.brand };
        }
        if (this.slides.length === 0) this._defaultSlide();
        if (this.selectedSlideIndex >= this.slides.length) this.selectedSlideIndex = 0;
        this.dirty = false;
        this._history.clear();
    }

    toSavePayload(): object {
        return {
            slides:             this.slides,
            selectedSlideIndex: this.selectedSlideIndex,
            selectedElementId:  this.selectedElementId,
            brand:              this.brand,
        };
    }

    // ── Slides ────────────────────────────────────────────────────────────

    async addSlide(bg: string = '#ffffff'): Promise<void> {
        this._pushUndo();
        this.slides.push(this._normalizeSlide({ bg, elements: [] }));
        this.selectedSlideIndex = this.slides.length - 1;
        this.selectedElementId = null;
        this.dirty = true;
        await this._render();
    }

    async deleteSlide(index: number): Promise<void> {
        if (this.slides.length <= 1) { await this._error('슬라이드가 하나 이상 있어야 합니다.'); return; }
        this._pushUndo();
        this.slides.splice(index, 1);
        this.selectedSlideIndex = Math.min(index, this.slides.length - 1);
        this.selectedElementId = null;
        this.dirty = true;
        await this._render();
    }

    async selectSlide(index: number): Promise<void> {
        if (index < 0 || index >= this.slides.length) return;
        this.selectedSlideIndex = index;
        this.selectedElementId = null;
        this.dirty = true;
        await this._render();
    }

    async moveSlide(from: number, to: number): Promise<void> {
        if (to < 0 || to >= this.slides.length) return;
        this._pushUndo();
        const [slide] = this.slides.splice(from, 1);
        this.slides.splice(to, 0, slide);
        this.selectedSlideIndex = to;
        this.dirty = true;
        await this._render();
    }

    async duplicateSlide(index: number): Promise<void> {
        if (index < 0 || index >= this.slides.length) return;
        this._pushUndo();
        const copy = this._normalizeSlide(JSON.parse(JSON.stringify(this.slides[index])));
        copy.id = this._uid();
        copy.elements = copy.elements.map(e => ({ ...e, id: this._uid() }));
        this.slides.splice(index + 1, 0, copy);
        this.selectedSlideIndex = index + 1;
        this.selectedElementId = null;
        this.dirty = true;
        await this._render();
    }

    async setSlideBackground(bg: string): Promise<void> {
        if (!this.currentSlide) return;
        this._pushUndo();
        this.currentSlide.bg = bg;
        this.dirty = true;
        await this._render();
    }

    // ── Elements ──────────────────────────────────────────────────────────

    async addElement(type: SlideElement['type'], overrides: Partial<SlideElement> = {}): Promise<void> {
        if (!this.currentSlide) return;
        this._pushUndo();
        const base = this._defaultElement(type);
        const el: SlideElement = { ...base, ...overrides, id: this._uid(), type };
        this.currentSlide.elements.push(el);
        this.selectedElementId = el.id;
        this.dirty = true;
        await this._render();
    }

    async deleteSelectedElement(): Promise<void> {
        if (!this.currentSlide || !this.selectedElementId) return;
        const el = this.selectedElement;
        if (el?.locked) { await this._error('잠긴 요소는 삭제할 수 없습니다.'); return; }
        this._pushUndo();
        this.currentSlide.elements = this.currentSlide.elements.filter(e => e.id !== this.selectedElementId);
        this.selectedElementId = null;
        this.dirty = true;
        await this._render();
    }

    async selectElement(id: string | null): Promise<void> {
        this.selectedElementId = id;
        await this._render();
    }

    async deselectElement(): Promise<void> {
        this.selectedElementId = null;
        await this._render();
    }

    async updateElement(id: string, props: Partial<SlideElement>): Promise<void> {
        const el = this.currentSlide?.elements.find(e => e.id === id);
        if (!el) return;
        if (el.locked) { await this._error('잠긴 요소는 수정할 수 없습니다.'); return; }
        this._pushUndo();
        Object.assign(el, props);
        this.dirty = true;
        await this._render();
    }

    async toggleLockElement(id: string): Promise<void> {
        const el = this.currentSlide?.elements.find(e => e.id === id);
        if (!el) return;
        this._pushUndo();
        el.locked = !el.locked;
        this.dirty = true;
        await this._render();
    }

    async bringForward(id: string): Promise<void> {
        const els = this.currentSlide?.elements;
        if (!els) return;
        const el = els.find(e => e.id === id);
        if (!el) return;
        const maxZ = Math.max(...els.map(e => e.zIndex));
        if (el.zIndex < maxZ) { this._pushUndo(); el.zIndex += 1; this.dirty = true; await this._render(); }
    }

    async sendBackward(id: string): Promise<void> {
        const els = this.currentSlide?.elements;
        if (!els) return;
        const el = els.find(e => e.id === id);
        if (!el) return;
        const minZ = Math.min(...els.map(e => e.zIndex));
        if (el.zIndex > minZ) { this._pushUndo(); el.zIndex -= 1; this.dirty = true; await this._render(); }
    }

    // ── Drag ──────────────────────────────────────────────────────────────

    onElementMouseDown(event: MouseEvent, el: SlideElement): void {
        if (el.locked) return;
        event.stopPropagation();
        this._drag = { el, startX: event.clientX, startY: event.clientY, ox: el.x, oy: el.y };
        window.addEventListener('mousemove', this._boundMouseMove);
        window.addEventListener('mouseup',   this._boundMouseUp);
    }

    // ── Resize ────────────────────────────────────────────────────────────

    onResizeMouseDown(event: MouseEvent, el: SlideElement, handle: string): void {
        if (el.locked) return;
        event.stopPropagation(); event.preventDefault();
        this._resize = { el, handle, startX: event.clientX, startY: event.clientY, ow: el.w, oh: el.h, ox: el.x, oy: el.y };
        window.addEventListener('mousemove', this._boundMouseMove);
        window.addEventListener('mouseup',   this._boundMouseUp);
    }

    // ── Templates ─────────────────────────────────────────────────────────

    async applyTemplate(template: { bg?: string; elements?: Partial<SlideElement>[] }): Promise<void> {
        if (!this.currentSlide) return;
        this._pushUndo();
        if (template.bg) this.currentSlide.bg = template.bg;
        if (template.elements) {
            this.currentSlide.elements = template.elements.map(e => ({
                ...this._defaultElement(e.type || 'text'),
                ...e,
                id: this._uid(),
            }));
        }
        this.selectedElementId = null;
        this.dirty = true;
        await this._render();
    }

    // ── Brand Kit ─────────────────────────────────────────────────────────

    async applyBrandToCurrentSlide(): Promise<void> {
        if (!this.currentSlide) return;
        this._pushUndo();
        this.currentSlide.bg = this.brand.secondaryColor;
        this.currentSlide.elements.forEach(el => {
            if (el.type === 'text') {
                el.color       = this.brand.primaryColor;
                el.fontFamily  = this.brand.fontFamily;
            }
            if (el.type === 'shape') {
                el.fill   = this.brand.primaryColor;
                el.stroke = this.brand.secondaryColor;
            }
        });
        this.dirty = true;
        await this._render();
    }

    async updateBrand(props: Partial<BrandKit>): Promise<void> {
        Object.assign(this.brand, props);
        this.dirty = true;
        await this._render();
    }

    // ── Undo / Redo ──────────────────────────────────────────────────────

    async undo(): Promise<void> {
        const snap = this._history.undo(this._snapshot());
        if (!snap) return;
        this._applySnapshot(snap);
        this.dirty = true;
        await this._render();
    }

    async redo(): Promise<void> {
        const snap = this._history.redo(this._snapshot());
        if (!snap) return;
        this._applySnapshot(snap);
        this.dirty = true;
        await this._render();
    }

    /** Attach global keyboard shortcuts. Call in ngOnInit. Returns the handler for removeEventListener. */
    bindKeyboard(): (e: KeyboardEvent) => void {
        const handler = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable) return;
            const meta = e.ctrlKey || e.metaKey;
            if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); void this.deleteSelectedElement(); }
            else if (meta && e.key === 'z' && !e.shiftKey) { e.preventDefault(); void this.undo(); }
            else if (meta && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); void this.redo(); }
        };
        window.addEventListener('keydown', handler);
        return handler;
    }

    /** Call in ngOnDestroy. */
    destroy(): void {
        window.removeEventListener('mousemove', this._boundMouseMove);
        window.removeEventListener('mouseup',   this._boundMouseUp);
    }

    // ── Style helpers (use in templates) ─────────────────────────────────

    elementStyle(el: SlideElement): { [k: string]: string | number } {
        const style: { [k: string]: string | number } = {
            position:   'absolute',
            left:       `${el.x}px`,
            top:        `${el.y}px`,
            width:      `${el.w}px`,
            height:     `${el.h}px`,
            zIndex:     el.zIndex,
            userSelect: 'none',
            cursor:     el.locked ? 'not-allowed' : 'move',
        };
        if (el.type === 'text') {
            Object.assign(style, {
                fontSize:       `${el.fontSize || 16}px`,
                fontWeight:     el.fontWeight || 'normal',
                fontFamily:     el.fontFamily || 'sans-serif',
                color:          el.color || '#000000',
                textAlign:      el.textAlign || 'left',
                fontStyle:      el.italic ? 'italic' : 'normal',
                textDecoration: el.underline ? 'underline' : 'none',
                overflowWrap:   'break-word',
            });
        }
        if (el.type === 'image') {
            Object.assign(style, { overflow: 'hidden' });
        }
        if (el.type === 'shape') {
            Object.assign(style, {
                background:   el.fill || '#e2e8f0',
                border:       `${el.strokeWidth ?? 1}px solid ${el.stroke || 'transparent'}`,
                borderRadius: el.shapeType === 'circle' ? '50%' : `${el.borderRadius ?? 0}px`,
            });
        }
        if (this.selectedElementId === el.id) {
            (style as any).outline = '2px solid #3b82f6';
        }
        return style;
    }

    slideThumbStyle(slide: Slide): { [k: string]: string } {
        return { background: slide.bg || '#ffffff', width: '100%', aspectRatio: '16/9', position: 'relative', overflow: 'hidden' };
    }

    // ── Private helpers ───────────────────────────────────────────────────

    private _uid(): string { return Math.random().toString(36).slice(2, 10) + Date.now().toString(36); }

    private _normalizeSlide(s: any): Slide {
        return {
            id:       s?.id || this._uid(),
            bg:       s?.bg || '#ffffff',
            elements: (s?.elements || []).map((e: any) => this._normalizeElement(e)),
        };
    }

    private _normalizeElement(e: any): SlideElement {
        return {
            id:          e.id || this._uid(),
            type:        e.type || 'text',
            x:           Number(e.x ?? 60),
            y:           Number(e.y ?? 60),
            w:           Number(e.w ?? 200),
            h:           Number(e.h ?? 80),
            zIndex:      Number(e.zIndex ?? 1),
            locked:      !!e.locked,
            text:        e.text ?? '',
            fontSize:    Number(e.fontSize ?? 16),
            fontWeight:  e.fontWeight ?? 'normal',
            fontFamily:  e.fontFamily ?? 'sans-serif',
            color:       e.color ?? '#000000',
            textAlign:   e.textAlign ?? 'left',
            italic:      !!e.italic,
            underline:   !!e.underline,
            src:         e.src ?? '',
            objectFit:   e.objectFit ?? 'cover',
            shapeType:   e.shapeType ?? 'rect',
            fill:        e.fill ?? '#e2e8f0',
            stroke:      e.stroke ?? 'transparent',
            strokeWidth: Number(e.strokeWidth ?? 1),
            borderRadius: Number(e.borderRadius ?? 0),
        };
    }

    private _defaultElement(type: SlideElement['type'] | string): SlideElement {
        const base: SlideElement = {
            id: '', type: (type as any) || 'text', x: 60, y: 60, w: 200, h: 80, zIndex: 1, locked: false,
            text: '텍스트를 입력하세요', fontSize: 16, fontWeight: 'normal', fontFamily: 'sans-serif',
            color: '#000000', textAlign: 'left', italic: false, underline: false,
            src: '', objectFit: 'cover',
            shapeType: 'rect', fill: '#e2e8f0', stroke: 'transparent', strokeWidth: 1, borderRadius: 0,
        };
        if (type === 'image') { base.w = 320; base.h = 200; }
        if (type === 'shape') { base.w = 120; base.h = 120; }
        return base;
    }

    private _defaultSlide(): void {
        this.slides = [this._normalizeSlide({ bg: '#ffffff', elements: [] })];
        this.selectedSlideIndex = 0;
    }

    private _snapshot(): CanvasSnapshot {
        return {
            slides:             JSON.parse(JSON.stringify(this.slides)),
            selectedSlideIndex: this.selectedSlideIndex,
            selectedElementId:  this.selectedElementId,
        };
    }

    private _pushUndo(): void { this._history.push(this._snapshot()); }

    private _applySnapshot(snap: CanvasSnapshot): void {
        this.slides             = (snap.slides || []).map(s => this._normalizeSlide(s));
        if (this.slides.length === 0) this._defaultSlide();
        this.selectedSlideIndex = Math.min(Math.max(0, snap.selectedSlideIndex ?? 0), this.slides.length - 1);
        this.selectedElementId  = snap.selectedElementId ?? null;
    }

    private _onMouseMove(e: MouseEvent): void {
        if (this._drag) {
            const el = this._drag.el;
            el.x = Math.max(0, this._drag.ox + (e.clientX - this._drag.startX));
            el.y = Math.max(0, this._drag.oy + (e.clientY - this._drag.startY));
            void this._render();
        } else if (this._resize) {
            const { el, handle, startX, startY, ow, oh, ox, oy } = this._resize;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (handle.includes('e')) el.w = Math.max(40, ow + dx);
            if (handle.includes('s')) el.h = Math.max(20, oh + dy);
            if (handle.includes('w')) { el.w = Math.max(40, ow - dx); el.x = ox + dx; }
            if (handle.includes('n')) { el.h = Math.max(20, oh - dy); el.y = oy + dy; }
            void this._render();
        }
    }

    private _onMouseUp(): void {
        if (this._drag)   { this._drag.el.x = Math.round(this._drag.el.x);   this._drag.el.y = Math.round(this._drag.el.y);   this._drag = null; this.dirty = true; }
        if (this._resize) { this._resize.el.w = Math.round(this._resize.el.w); this._resize.el.h = Math.round(this._resize.el.h); this._resize = null; this.dirty = true; }
        window.removeEventListener('mousemove', this._boundMouseMove);
        window.removeEventListener('mouseup',   this._boundMouseUp);
        void this._render();
    }

    private async _error(msg: string): Promise<void> {
        if (this._onError) await this._onError(msg);
        else console.warn('[SlideCanvasController]', msg);
    }
}
